/* eslint-disable */
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { MaterialReactTable } from "material-react-table";
import { MenuItem } from "@mui/material";

import TabsHeader from "components/Common/TabsHeader/TabsHeader";
import { commonHeaderLinkLists } from "helper/WebhookHelper/WebhookHelper";

import { getData, putData } from "utils/Gateway";
import * as url from "helper/UrlHelper";

import WebhookListHeader from "../WebhookListHeader/WebhookListHeader";
import SaveWebhookOffcanvas from "../Offcanvas/SaveWebhookOffcanvas";
import AlertNotification from "components/Common/AlertNotification/AlertNotification";
import DeleteWarningModal from "components/Common/Modal/DeleteWarningModal";
import LoadingSkeleton from "components/Common/ListLoadingSkeleton/LoadingSkeleton";


const WebhookListBody = () => {
  const { t } = useTranslation();
  const userInfo = JSON.parse(localStorage.getItem("userInfo"));

  const commonHeaderObject = commonHeaderLinkLists(t);

  const breadcrumbText = [
    { title: t("Webhook"), link: "/admin/webhook/list" },
    { title: t("Config") },
  ];

  const [webhookList, setWebhookList] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [editWebhookId, setEditWebhookId] = useState(null);

  const [rowSelection, setRowSelection] = useState({});
  const [selectedWebhookIds, setSelectedWebhookIds] = useState([]);

  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [messageType, setMessageType] = useState("");

  const getAllWebhooks = async () => {
    try {
      setIsLoading(true);
      const requestUrl = url.API_BASE_URL + url.API_GET_ALL_WEBHOOKS;
      const response = await getData(requestUrl);
      if (response.status) {
        setWebhookList(response.data);
        setMessageType("success");
      } else {
        setMessageType("error");
      }
      setAlertMessage(response.message);
    } catch (err) {
      setAlertMessage(err.message);
      setMessageType("error");
    } finally {
      setIsLoading(false);
      setShowAlert(true);
    }
  };

  const openEditOffcanvas = (id) => {
    setEditWebhookId(id);
    const el = document.querySelector("#saveWebhookOffcanvas");
    if (el) {
      const offcanvas = new window.bootstrap.Offcanvas(el);
      offcanvas.show();
    }
  };

  const openAddOffcanvas = () => {
    setEditWebhookId(null);
    const el = document.querySelector("#saveWebhookOffcanvas");
    if (el) {
      const offcanvas = new window.bootstrap.Offcanvas(el);
      offcanvas.show();
    }
  };

  const deleteWebhookHandler = async () => {
    if (selectedWebhookIds.length === 0) return;
    for (const id of selectedWebhookIds) {
      try {
        const requestUrl = url.API_BASE_URL + url.API_DELETE_WEBHOOK + `/${id}`;
        await putData(requestUrl, { status: "0" });
      } catch (err) {
        console.log(err.message);
      }
    }
    setRowSelection({});
    setSelectedWebhookIds([]);
    setMessageType("success");
    setAlertMessage(t("Webhook(s) deleted"));
    setShowAlert(true);
    getAllWebhooks();
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: "#",
        header: t("Actions"),
        size: 70,
        enableSorting: false,
        enableColumnActions: false,
        enableColumnDragging: false,
        Cell: ({ row }) => (
          <button
            className="action_btn_mui"
            onClick={() => openEditOffcanvas(row.original._id)}
          >
            <span className="d-block material-symbols-outlined horz_icon">
              more_horiz
            </span>
          </button>
        ),
      },
      {
        accessorKey: "name",
        header: t("Name"),
        size: 180,
      },
      {
        accessorKey: "provider",
        header: t("Provider"),
        size: 120,
        Cell: ({ cell }) => (
          <span className="badge bg-primary text-capitalize">
            {cell.getValue()}
          </span>
        ),
      },
      {
        accessorKey: "webhookUrl",
        header: t("URL"),
        size: 260,
        Cell: ({ cell }) => (
          <code className="fs-xs text-muted">{cell.getValue()}</code>
        ),
      },
      {
        accessorKey: "company",
        header: t("Company"),
        size: 160,
        Cell: ({ row }) => row.original.company?.name || "—",
      },
      {
        accessorKey: "user",
        header: t("User"),
        size: 180,
        Cell: ({ row }) => {
          const u = row.original.user;
          if (!u) return "—";
          return `${u.name || ""} ${u.surname || ""}`.trim() || u.email;
        },
      },
      {
        accessorKey: "active",
        header: t("Active"),
        size: 90,
        Cell: ({ cell }) =>
          cell.getValue() ? (
            <span className="badge bg-success">{t("Yes")}</span>
          ) : (
            <span className="badge bg-secondary">{t("No")}</span>
          ),
      },
      {
        accessorKey: "settings",
        header: t("Settings"),
        size: 180,
        Cell: ({ row }) => {
          const s = row.original.settings || {};
          return (
            <div className="d-flex flex-column gap-1">
              {s.autoCreateLead && (
                <span className="badge bg-info fs-xs">{t("Auto Lead")}</span>
              )}
              {s.attachRecording && (
                <span className="badge bg-warning text-dark fs-xs">
                  {t("Recording")}
                </span>
              )}
            </div>
          );
        },
      },
      {
        accessorKey: "createdAt",
        header: t("Created"),
        size: 130,
        Cell: ({ cell }) =>
          cell.getValue()
            ? new Date(cell.getValue()).toLocaleDateString()
            : "—",
      },
    ],
    [t]
  );

  // sync row selection → selectedWebhookIds
  useEffect(() => {
    const ids = Object.keys(rowSelection).filter((k) => rowSelection[k]);
    setSelectedWebhookIds(ids.length > 0 ? ids : []);
  }, [rowSelection]);

  useEffect(() => {
    getAllWebhooks();
  }, []);

  const onAlertClose = () => {
    setShowAlert(false);
    setAlertMessage("");
    setMessageType("");
  };

  return (
    <div id="content_wrapper">
      <section className="crm-wrapper bg-white pb-5">
        <TabsHeader
          commonHeaderObject={commonHeaderObject}
          activeOption={t("Webhook Config")}
        />

        <div className="container-fluid px-lg-5">
          <WebhookListHeader
            breadcrumbText={breadcrumbText}
            reloadList={getAllWebhooks}
            selectedWebhookIds={selectedWebhookIds}
            openAddOffcanvas={openAddOffcanvas}
          />

          <div className="table-wrapper">
            {isLoading ? (
              <LoadingSkeleton />
            ) : (
              <MaterialReactTable
                columns={columns}
                data={webhookList}
                enableRowSelection
                getRowId={(row) => row._id}
                onRowSelectionChange={setRowSelection}
                state={{ rowSelection }}
                defaultColumn={{ minSize: 20, maxSize: 300, size: 50 }}
                muiTableContainerProps={{ sx: { maxHeight: "60vh" } }}
                enableStickyHeader
              />
            )}
          </div>
        </div>
      </section>

      {/* offcanvas */}
      <SaveWebhookOffcanvas
        editWebhookId={editWebhookId}
        afterSave={getAllWebhooks}
        setShowAlert={setShowAlert}
        setAlertMessage={setAlertMessage}
        setMessageType={setMessageType}
      />

      {/* delete warning modal */}
      <DeleteWarningModal
        onCancelDelete={() => {
          setRowSelection({});
          setSelectedWebhookIds([]);
        }}
        onDelete={deleteWebhookHandler}
        moduleName={t("webhook")}
      />

      {/* alert */}
      {showAlert && (
        <AlertNotification
          showAlert={showAlert}
          message={alertMessage}
          alertType={messageType}
          onClose={onAlertClose}
        />
      )}
    </div>
  );
};

export default WebhookListBody;
